import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { X, ChevronDown } from 'lucide-react';
import { apiService } from '../../services/api';
import AssetLinksPanel from './AssetLinksPanel';

type EmergingAssetRow = {
  id: number;
  asset_name: string;
  company_name?: string | null;
  status?: string | null;
  trial_count?: number | null;
};

/** Lowercase, drop everything but [a-z0-9], trim. Used for grouping near-duplicates. */
function normalizeName(s: string | null | undefined): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '').trim();
}

/** Tokenize into lowercase alphanumeric tokens. */
function tokenize(s: string | null | undefined): string[] {
  return (s || '').toLowerCase().split(/[^a-z0-9]+/).filter((tok) => tok.length > 1);
}

/** Levenshtein distance between two short strings (used only on normalized forms). */
function lev(a: string, b: string): number {
  if (a === b) return 0;
  const al = a.length;
  const bl = b.length;
  if (al === 0) return bl;
  if (bl === 0) return al;
  const dp: number[] = new Array(bl + 1);
  for (let j = 0; j <= bl; j++) dp[j] = j;
  for (let i = 1; i <= al; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= bl; j++) {
      const tmp = dp[j];
      dp[j] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[j], dp[j - 1]);
      prev = tmp;
    }
  }
  return dp[bl];
}

/** Group assets that look like duplicates of each other.
 *  Heuristics combined with union-find:
 *   1. Same normalized form  (e.g. "Drug-X" vs "Drug X" vs "drugX")
 *   2. Levenshtein <= 2 between normalized forms (both >=4 chars), e.g. "drugx" vs "drugxs"
 *   3. Token overlap: one name's full token set is a subset of the other AND
 *      the smaller has >= 1 distinctive (>=4 char) token.
 */
function buildDuplicateClusters(assets: EmergingAssetRow[]): EmergingAssetRow[][] {
  const n = assets.length;
  const parent = new Array(n).fill(0).map((_, i) => i);
  const find = (x: number): number => (parent[x] === x ? x : (parent[x] = find(parent[x])));
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  };

  const norms = assets.map((a) => normalizeName(a.asset_name));
  const toks = assets.map((a) => new Set(tokenize(a.asset_name)));

  // 1) bucket by exact normalized form
  const byNorm = new Map<string, number[]>();
  norms.forEach((n0, i) => {
    if (!n0) return;
    const arr = byNorm.get(n0);
    if (arr) arr.push(i);
    else byNorm.set(n0, [i]);
  });
  for (const arr of byNorm.values()) {
    for (let k = 1; k < arr.length; k++) union(arr[0], arr[k]);
  }

  // 2) edit-distance pass (only within same first 2 chars to keep it cheap)
  const byPrefix = new Map<string, number[]>();
  norms.forEach((n0, i) => {
    if (n0.length < 4) return;
    const k = n0.slice(0, 2);
    const arr = byPrefix.get(k);
    if (arr) arr.push(i);
    else byPrefix.set(k, [i]);
  });
  for (const arr of byPrefix.values()) {
    if (arr.length < 2) continue;
    for (let i = 0; i < arr.length; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        const a = norms[arr[i]];
        const b = norms[arr[j]];
        if (Math.abs(a.length - b.length) > 2) continue;
        if (lev(a, b) <= 2) union(arr[i], arr[j]);
      }
    }
  }

  // 3) token-subset match: e.g. "Drug X" vs "Drug X acetate"
  for (let i = 0; i < n; i++) {
    const ti = toks[i];
    if (ti.size === 0) continue;
    for (let j = i + 1; j < n; j++) {
      const tj = toks[j];
      if (tj.size === 0) continue;
      const small = ti.size <= tj.size ? ti : tj;
      const big = small === ti ? tj : ti;
      let allIn = true;
      let distinctive = false;
      for (const tok of small) {
        if (!big.has(tok)) {
          allIn = false;
          break;
        }
        if (tok.length >= 4) distinctive = true;
      }
      if (allIn && distinctive && small.size >= 1) union(i, j);
    }
  }

  const clusters = new Map<number, EmergingAssetRow[]>();
  for (let i = 0; i < n; i++) {
    const r = find(i);
    const arr = clusters.get(r);
    if (arr) arr.push(assets[i]);
    else clusters.set(r, [assets[i]]);
  }
  return [...clusters.values()].filter((arr) => arr.length > 1);
}

interface AssetComboBoxProps {
  options: EmergingAssetRow[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  excludeId?: number;
  placeholder?: string;
}

const AssetComboBox: React.FC<AssetComboBoxProps> = ({ options, selectedId, onSelect, excludeId, placeholder }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const selectedName = useMemo(
    () => options.find((o) => o.id === selectedId)?.asset_name ?? '',
    [options, selectedId]
  );

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = excludeId != null ? options.filter((o) => o.id !== excludeId) : options;
    if (!q) return base.slice(0, 50);
    const tokens = q.split(/\s+/).filter(Boolean);
    return base
      .filter((o) => {
        const name = (o.asset_name || '').toLowerCase();
        const co = (o.company_name || '').toLowerCase();
        return tokens.every((t) => name.includes(t) || co.includes(t));
      })
      .slice(0, 50);
  }, [options, query, excludeId]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  const commit = (id: number) => {
    onSelect(id);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt) commit(opt.id);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const displayValue = open ? query : selectedName;

  return (
    <div ref={containerRef} className="relative w-full">
      <input
        value={displayValue}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder || 'Type to search assets…'}
        className="w-full rounded-md border border-slate-200 bg-white px-2 py-1 pr-7 text-[11px] text-slate-800 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        spellCheck={false}
        autoComplete="off"
      />
      <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
      {open && (
        <div className="absolute z-30 mt-1 max-h-64 w-[260px] overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-2 py-1.5 text-[11px] text-slate-500">No matches</div>
          ) : (
            filtered.map((opt, idx) => (
              <button
                key={opt.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(opt.id);
                }}
                onMouseEnter={() => setHighlight(idx)}
                className={`w-full text-left px-2 py-1.5 text-[11px] flex items-center justify-between gap-2 ${
                  idx === highlight ? 'bg-indigo-50 text-indigo-800' : 'text-slate-700 hover:bg-slate-50'
                } ${selectedId === opt.id ? 'font-semibold' : ''}`}
              >
                <span className="truncate">
                  {opt.asset_name}
                  {opt.company_name ? <span className="text-slate-400"> · {opt.company_name}</span> : null}
                </span>
                <span className="text-[10px] text-slate-400">#{opt.id}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const ManageEmergingAssets: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const focusParam = searchParams.get('focus');
  const focusId = focusParam && !Number.isNaN(Number(focusParam)) ? Number(focusParam) : null;

  const [assets, setAssets] = useState<EmergingAssetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editNames, setEditNames] = useState<Record<number, string>>({});
  const [mergeTargets, setMergeTargets] = useState<Record<number, number | null>>({});
  const [splitNames, setSplitNames] = useState<Record<number, string>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [expandedLinksId, setExpandedLinksId] = useState<number | null>(null);
  const [indicationOptions, setIndicationOptions] = useState<Array<{ id: string; name: string }>>([]);

  /** Cluster picker state: which cluster index → which row id is selected as canonical. */
  const [clusterTarget, setClusterTarget] = useState<Record<number, number>>({});
  const [dismissedClusters, setDismissedClusters] = useState<Set<string>>(new Set());
  const [clusterBusy, setClusterBusy] = useState<number | null>(null);
  const [clusterSection, setClusterSection] = useState(true);
  const focusRowRef = useRef<HTMLTableRowElement | null>(null);

  const loadAssets = async () => {
    try {
      setLoading(true);
      setError(null);
      const [response, indications] = await Promise.all([
        apiService.fetchSuperuserEmergingAssets(),
        apiService.fetchEmergingIndications(),
      ]);
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to load assets');
      }
      setAssets(response.data || []);
      setIndicationOptions(
        (indications || []).map((ind: { id: string; name: string }) => ({ id: String(ind.id), name: ind.name })),
      );
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e?.response?.data?.error || e?.message || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssets();
  }, []);

  const filteredAssets = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return assets;
    const tokens = q.split(/\s+/).filter(Boolean);
    return assets.filter((asset) => {
      const haystack = `${asset.asset_name || ''} ${asset.company_name || ''} ${asset.status || ''}`.toLowerCase();
      return tokens.every((t) => haystack.includes(t));
    });
  }, [assets, searchQuery]);

  const allClusters = useMemo(
    () => (clusterSection ? buildDuplicateClusters(assets) : []),
    [assets, clusterSection],
  );

  const visibleClusters = useMemo(() => {
    return allClusters
      .map((cluster, idx) => ({ idx, cluster, key: cluster.map((c) => c.id).sort((a, b) => a - b).join('-') }))
      .filter(({ key }) => !dismissedClusters.has(key));
  }, [allClusters, dismissedClusters]);

  /** Auto-select the row with the highest trial_count as canonical for each cluster. */
  useEffect(() => {
    setClusterTarget((prev) => {
      const next = { ...prev };
      visibleClusters.forEach(({ idx, cluster }) => {
        if (next[idx] != null && cluster.some((c) => c.id === next[idx])) return;
        const sorted = [...cluster].sort(
          (a, b) => (b.trial_count ?? 0) - (a.trial_count ?? 0) || a.id - b.id
        );
        next[idx] = sorted[0]?.id;
      });
      return next;
    });
  }, [visibleClusters]);

  useEffect(() => {
    if (focusId) setExpandedLinksId(focusId);
  }, [focusId]);

  useEffect(() => {
    if (!focusId || !focusRowRef.current) return;
    focusRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusId, filteredAssets]);

  const clearFocus = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('focus');
    setSearchParams(next, { replace: true });
  };

  const handleRename = async (asset: EmergingAssetRow) => {
    const nextName = (editNames[asset.id] ?? asset.asset_name).trim();
    if (!nextName || nextName === asset.asset_name) return;
    try {
      setSavingId(asset.id);
      const response = await apiService.renameEmergingAsset({ asset_id: asset.id, asset_name: nextName });
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to rename asset');
      }
      await loadAssets();
      setEditNames((prev) => ({ ...prev, [asset.id]: nextName }));
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e?.response?.data?.error || e?.message || 'Failed to rename asset');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (assetId: number) => {
    if (!window.confirm('Delete this asset? This will unlink related records.')) return;
    try {
      setSavingId(assetId);
      const response = await apiService.deleteEmergingAsset({ asset_id: assetId });
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to delete asset');
      }
      await loadAssets();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e?.response?.data?.error || e?.message || 'Failed to delete asset');
    } finally {
      setSavingId(null);
    }
  };

  const handleMerge = async (sourceId: number) => {
    const targetId = mergeTargets[sourceId];
    if (!targetId || Number(targetId) === sourceId) {
      setError('Select a different target asset to merge into.');
      return;
    }
    const target = assets.find((a) => a.id === Number(targetId));
    const source = assets.find((a) => a.id === sourceId);
    if (!window.confirm(
      `Merge "${source?.asset_name}" (#${sourceId}) into "${target?.asset_name}" (#${targetId})?\n` +
      `All trials, catalysts, indication statuses, and company links will move to the target. ` +
      `The source row will be deleted.`
    )) {
      return;
    }
    try {
      setSavingId(sourceId);
      const response = await apiService.mergeEmergingAssets({
        source_asset_id: sourceId,
        target_asset_id: Number(targetId),
      });
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to merge assets');
      }
      await loadAssets();
      setMergeTargets((prev) => ({ ...prev, [sourceId]: null }));
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e?.response?.data?.error || e?.message || 'Failed to merge assets');
    } finally {
      setSavingId(null);
    }
  };

  const handleClusterMerge = async (clusterIdx: number, cluster: EmergingAssetRow[]) => {
    const targetId = clusterTarget[clusterIdx];
    if (!targetId) {
      setError('Pick a canonical asset for this cluster first.');
      return;
    }
    const target = cluster.find((c) => c.id === targetId);
    const sources = cluster.filter((c) => c.id !== targetId).map((c) => c.id);
    if (sources.length === 0) {
      setError('No source assets to merge in this cluster.');
      return;
    }
    if (!window.confirm(
      `Merge ${sources.length} asset(s) into "${target?.asset_name}" (#${targetId})?\n\n` +
      cluster
        .filter((c) => c.id !== targetId)
        .map((c) => ` • ${c.asset_name} (#${c.id})${c.trial_count ? ` — ${c.trial_count} trials` : ''}`)
        .join('\n') +
      `\n\nAll references move to the target. Source rows will be deleted.`
    )) {
      return;
    }
    try {
      setClusterBusy(clusterIdx);
      const response = await apiService.mergeEmergingAssets({
        source_asset_ids: sources,
        target_asset_id: targetId,
      });
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to merge cluster');
      }
      await loadAssets();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e?.response?.data?.error || e?.message || 'Failed to merge cluster');
    } finally {
      setClusterBusy(null);
    }
  };

  const handleSplit = async (assetId: number) => {
    const raw = splitNames[assetId] || '';
    const names = raw
      .split(/[\n,]+/)
      .map((name) => name.trim())
      .filter(Boolean);
    if (names.length < 2) {
      setError('Provide at least two asset names to split.');
      return;
    }
    if (!window.confirm('Split this asset into multiple assets? This will remove the original asset.')) {
      return;
    }
    try {
      setSavingId(assetId);
      const response = await apiService.splitEmergingAsset({ asset_id: assetId, asset_names: names });
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to split asset');
      }
      await loadAssets();
      setSplitNames((prev) => ({ ...prev, [assetId]: '' }));
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e?.response?.data?.error || e?.message || 'Failed to split asset');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-8 space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Manage Emerging Assets</h1>
          <p className="text-sm text-slate-500">Rename, merge, or delete assets. Use Indication links to set per-disease status (Approved vs Ph2, etc.).</p>
        </div>
        <div className="w-full md:w-72">
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search assets..."
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700 flex items-start justify-between gap-3">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-rose-500 hover:text-rose-700"
            aria-label="Dismiss error"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {focusId && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center justify-between gap-3">
          <span>Highlighting asset #{focusId} (scrolled into view).</span>
          <button type="button" onClick={clearFocus} className="text-amber-700 hover:text-amber-900 font-semibold">
            Clear
          </button>
        </div>
      )}

      {!loading && (
        <section className="rounded-2xl border border-slate-200">
          <button
            type="button"
            onClick={() => setClusterSection((v) => !v)}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
          >
            <div>
              <h2 className="text-sm font-bold text-slate-900">
                Possible duplicates{' '}
                <span className="text-slate-400 font-medium">({visibleClusters.length} group{visibleClusters.length === 1 ? '' : 's'})</span>
              </h2>
              <p className="text-xs text-slate-500">
                Auto-detected by normalized name, edit distance, and shared tokens. Pick a canonical asset, then merge.
              </p>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${clusterSection ? 'rotate-180' : ''}`} />
          </button>
          {clusterSection && (
            <div className="border-t border-slate-100 p-4 space-y-3">
              {visibleClusters.length === 0 ? (
                <p className="text-sm text-slate-500">No likely duplicates detected.</p>
              ) : (
                visibleClusters.map(({ idx, cluster, key }) => {
                  const targetId = clusterTarget[idx];
                  return (
                    <div key={key} className="rounded-xl border border-slate-200 p-3 bg-slate-50/40">
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                          {cluster.length} similar names
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleClusterMerge(idx, cluster)}
                            disabled={clusterBusy === idx || !targetId}
                            className="px-3 py-1 rounded-md text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                          >
                            {clusterBusy === idx ? 'Merging…' : 'Merge into canonical'}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setDismissedClusters((prev) => {
                                const next = new Set(prev);
                                next.add(key);
                                return next;
                              })
                            }
                            className="px-2 py-1 rounded-md text-xs font-medium text-slate-500 hover:text-slate-800"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                        {cluster.map((asset) => (
                          <label
                            key={asset.id}
                            className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer ${
                              targetId === asset.id
                                ? 'border-indigo-400 bg-white ring-2 ring-indigo-100'
                                : 'border-slate-200 bg-white hover:border-slate-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`cluster-${idx}`}
                              checked={targetId === asset.id}
                              onChange={() => setClusterTarget((prev) => ({ ...prev, [idx]: asset.id }))}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-slate-900 truncate">{asset.asset_name}</div>
                              <div className="text-[11px] text-slate-500 mt-0.5">
                                #{asset.id}
                                {asset.company_name ? ` · ${asset.company_name}` : ''}
                                {asset.trial_count != null ? ` · ${asset.trial_count} trial${asset.trial_count === 1 ? '' : 's'}` : ''}
                                {targetId === asset.id ? ' · canonical' : ''}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </section>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Asset</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Company</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Indication status</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Trials</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredAssets.map((asset) => {
                const isFocused = focusId === asset.id;
                const linksOpen = expandedLinksId === asset.id;
                return (
                <React.Fragment key={asset.id}>
                <tr
                  ref={isFocused ? focusRowRef : undefined}
                  className={`hover:bg-slate-50 ${isFocused ? 'bg-amber-50/60 ring-2 ring-amber-300/60 ring-inset' : ''}`}
                >
                  <td className="px-3 py-2 w-[220px] max-w-[220px] align-top">
                    <div className="space-y-2">
                      <div
                        className="text-sm font-semibold text-slate-900 break-words"
                        title={asset.asset_name}
                      >
                        {asset.asset_name}
                        <span className="ml-2 text-[10px] font-normal text-slate-400">#{asset.id}</span>
                      </div>
                      <input
                        value={editNames[asset.id] ?? asset.asset_name}
                        onChange={(event) =>
                          setEditNames((prev) => ({ ...prev, [asset.id]: event.target.value }))
                        }
                        className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs"
                        placeholder="Rename asset"
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{asset.company_name || '—'}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs max-w-[280px] break-words">{asset.status || '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{asset.trial_count ?? '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-2 min-w-[260px]">
                      <button
                        onClick={() => handleRename(asset)}
                        disabled={savingId === asset.id}
                        className="px-2 py-1 rounded-md text-[11px] font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                      >
                        {savingId === asset.id ? 'Saving...' : 'Save Name'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setExpandedLinksId(linksOpen ? null : asset.id)}
                        className={`px-2 py-1 rounded-md text-[11px] font-semibold border ${
                          linksOpen
                            ? 'border-indigo-400 bg-indigo-50 text-indigo-800'
                            : 'border-indigo-200 text-indigo-700 hover:bg-indigo-50'
                        }`}
                      >
                        {linksOpen ? 'Hide links' : 'Indication links'}
                      </button>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <AssetComboBox
                            options={assets}
                            selectedId={mergeTargets[asset.id] ?? null}
                            onSelect={(id) =>
                              setMergeTargets((prev) => ({ ...prev, [asset.id]: id }))
                            }
                            excludeId={asset.id}
                            placeholder="Merge into… (type to search)"
                          />
                        </div>
                        <button
                          onClick={() => handleMerge(asset.id)}
                          disabled={savingId === asset.id || !mergeTargets[asset.id]}
                          className="px-2 py-1 rounded-md text-[11px] font-semibold border border-slate-200 text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                        >
                          Merge
                        </button>
                      </div>
                      <div className="space-y-1">
                        <input
                          value={splitNames[asset.id] ?? ''}
                          onChange={(event) =>
                            setSplitNames((prev) => ({ ...prev, [asset.id]: event.target.value }))
                          }
                          placeholder="Split into (comma or newline)"
                          className="w-full rounded-md border border-slate-200 px-2 py-1 text-[11px]"
                        />
                        <button
                          onClick={() => handleSplit(asset.id)}
                          disabled={savingId === asset.id}
                          className="w-full px-2 py-1 rounded-md text-[11px] font-semibold border border-slate-200 text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                        >
                          Split
                        </button>
                      </div>
                      <button
                        onClick={() => handleDelete(asset.id)}
                        disabled={savingId === asset.id}
                        className="px-2 py-1 rounded-md text-[11px] font-semibold border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-60"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
                {linksOpen && (
                  <tr className="bg-slate-50/50">
                    <td colSpan={5} className="px-3 pb-4">
                      <AssetLinksPanel
                        assetId={asset.id}
                        indicationOptions={indicationOptions}
                        onError={setError}
                      />
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ManageEmergingAssets;
